import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Heart, MapPin, Bed, Bath, Car, Square, Home, Star } from '@/lib/icons';
import { Link } from 'react-router-dom';
import { Property } from '@/types/marketplace';
import { useTenant } from '@/contexts/TenantContext';
import { useBranding } from '@/contexts/BrandingContext';
import { generatePropertySlug } from '@/utils/propertySlug';
import { cn } from '@/lib/utils';
import { LazyImage } from '@/components/ui/LazyImage';
import { useFavorites } from '@/hooks/useFavorites';
import { generateWebPWithFallback, preloadImage } from '@/utils/imageUtils';
import { ChevronLeft, ChevronRight } from 'lucide-react';

const PropertyImageCarousel = ({ 
  images, 
  alt, 
  className,
  imageClassName,
  priority = false
}: { 
  images: string[], 
  alt: string, 
  className?: string,
  imageClassName?: string,
  priority?: boolean 
}) => {
  const [currentIndex, setCurrentIndex] = React.useState(0);
  const [renderedIndices, setRenderedIndices] = React.useState<Set<number>>(() => new Set([0]));
  const [touchStart, setTouchStart] = React.useState<number | null>(null);
  const [touchEnd, setTouchEnd] = React.useState<number | null>(null);

  const minSwipeDistance = 50;

  // Helper to get optimized URL for a given image index
  const getOptimizedSrc = React.useCallback((imgUrl: string) => {
    return generateWebPWithFallback(imgUrl, 80, 600, 400);
  }, []);

  // Preload a specific slide index in background
  const preloadSlide = React.useCallback((idx: number) => {
    if (idx >= 0 && idx < images.length && images[idx]) {
      const { webp, fallback } = getOptimizedSrc(images[idx]);
      preloadImage(webp || fallback);
      setRenderedIndices(prev => {
        if (prev.has(idx)) return prev;
        const next = new Set(prev);
        next.add(idx);
        return next;
      });
    }
  }, [images, getOptimizedSrc]);

  // Preload slide 1 immediately if this is a priority card in initial viewport
  React.useEffect(() => {
    if (priority && images.length > 1) {
      preloadSlide(1);
    }
  }, [priority, images.length, preloadSlide]);

  // On hover or touch, preload next slides proactively
  const handleInteractionStart = React.useCallback(() => {
    preloadSlide(currentIndex + 1);
    preloadSlide(currentIndex + 2);
    if (currentIndex > 0) preloadSlide(currentIndex - 1);
  }, [currentIndex, preloadSlide]);

  const onTouchStart = (e: React.TouchEvent) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
    handleInteractionStart();
  };

  const onTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;
    
    if (isLeftSwipe) {
      const nextIdx = currentIndex === images.length - 1 ? 0 : currentIndex + 1;
      setCurrentIndex(nextIdx);
      preloadSlide(nextIdx + 1);
    } else if (isRightSwipe) {
      const prevIdx = currentIndex === 0 ? images.length - 1 : currentIndex - 1;
      setCurrentIndex(prevIdx);
      preloadSlide(prevIdx - 1);
    }
  };

  const goNext = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const nextIdx = currentIndex === images.length - 1 ? 0 : currentIndex + 1;
    setCurrentIndex(nextIdx);
    preloadSlide(nextIdx + 1);
    preloadSlide(nextIdx + 2);
  };

  const goPrev = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const prevIdx = currentIndex === 0 ? images.length - 1 : currentIndex - 1;
    setCurrentIndex(prevIdx);
    preloadSlide(prevIdx - 1);
  };

  if (!images || images.length === 0) return null;

  return (
    <div 
      className={cn("relative w-full h-full group/carousel overflow-hidden bg-muted/30", className)}
      onMouseEnter={handleInteractionStart}
      onPointerEnter={handleInteractionStart}
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
    >
      <div 
        className="flex w-full h-full transition-transform duration-500 ease-out"
        style={{ transform: `translateX(-${currentIndex * 100}%)` }}
      >
        {images.map((img, idx) => {
          // Render in DOM if it's the current slide, adjacent to current, or was preloaded
          const shouldRender = renderedIndices.has(idx) || Math.abs(idx - currentIndex) <= 1 || idx === 0;
          const optimized = shouldRender ? getOptimizedSrc(img) : null;
          const isCurrent = idx === currentIndex;

          return (
            <div key={idx} className="w-full h-full flex-shrink-0 relative overflow-hidden bg-muted/20">
              {shouldRender && optimized ? (
                <picture>
                  {optimized.webp && (
                    <source srcSet={optimized.webp} type="image/webp" />
                  )}
                  <img
                    src={optimized.fallback || optimized.webp || img}
                    alt={`${alt} - Foto ${idx + 1}`}
                    className={cn("w-full h-full object-cover", imageClassName)}
                    loading={isCurrent && priority ? 'eager' : 'lazy'}
                    decoding="async"
                    {...({ fetchpriority: isCurrent ? (priority ? 'high' : 'auto') : 'low' } as any)}
                    width={600}
                    height={400}
                  />
                </picture>
              ) : (
                <div className="w-full h-full bg-muted/30 animate-pulse" />
              )}
            </div>
          );
        })}
      </div>

      {images.length > 1 && (
        <>
          {/* Navigation Arrows */}
          <button 
            onClick={goPrev}
            className="absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-white/90 text-black flex items-center justify-center opacity-0 group-hover/carousel:opacity-100 transition-all hover:bg-white hover:scale-105 shadow-sm z-20"
            aria-label="Foto anterior"
          >
            <ChevronLeft size={18} strokeWidth={2.5} className="mr-0.5" />
          </button>
          
          <button 
            onClick={goNext}
            className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-white/90 text-black flex items-center justify-center opacity-0 group-hover/carousel:opacity-100 transition-all hover:bg-white hover:scale-105 shadow-sm z-20"
            aria-label="Próxima foto"
          >
            <ChevronRight size={18} strokeWidth={2.5} className="ml-0.5" />
          </button>

          {/* Pagination Dots */}
          <div className="absolute bottom-3 left-0 right-0 flex justify-center gap-1.5 z-20">
            {images.slice(0, 8).map((_, idx) => (
              <div 
                key={idx} 
                className={`h-1.5 rounded-full transition-all duration-300 shadow-sm ${idx === currentIndex ? 'bg-white w-4' : 'bg-white/70 w-1.5'}`}
              />
            ))}
            {images.length > 8 && <div className="h-1.5 w-1.5 rounded-full bg-white/70 shadow-sm" />}
          </div>
        </>
      )}
    </div>
  );
};

interface PropertyCardProps {
  property: Property;
  variant?: 'default' | 'compact';
  priority?: boolean;
}

export const PropertyCard = React.memo<PropertyCardProps>(
  ({ property, variant = 'default', priority = false }: PropertyCardProps) => {
    const { tenant } = useTenant();
    const { branding } = useBranding();
    const { isFavorite, toggleFavorite } = useFavorites();
    const isCompact = variant === 'compact';
    const cardStyle = branding?.property_card_style || 'modern';

    const formatPrice = (price: number, status: 'sale' | 'rent') => {
      const formattedPrice = new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(price);

      return status === 'rent' ? `${formattedPrice}/mês` : formattedPrice;
    };

    const getPropertyUrl = () => {
      const hostname = window.location.hostname;
      const tenantDomain = 'imobpanda.site';
      const cleanHost = hostname.replace(/^www\./, '');
      const isInSubdomain = cleanHost !== tenantDomain && cleanHost.endsWith(tenantDomain);

      if (property._imovelData) {
        const imovelForSlug = {
          ...property._imovelData,
          user_id: property._imovelData.user_id || '',
        };
        const slug = generatePropertySlug(imovelForSlug as any);

        if (isInSubdomain) {
          return `/imovel/${slug}`;
        }

        if (tenant?.subdomain) {
          return `/${tenant.subdomain}/imovel/${slug}`;
        }

        return `/imovel/${slug}`;
      }

      if (isInSubdomain) {
        return `/properties/${property.id}`;
      }

      const basePath = tenant?.subdomain ? `/${tenant.subdomain}` : '';
      return `${basePath}/properties/${property.id}`;
    };

    const adaptedProperty = {
      id: property.id,
      title: property.title,
      type: property.status,
      price: property.price,
      location: {
        address: property.location.split(',')[0] || '',
        city: property.location.split(',')[1]?.trim() || '',
        state: property.location.split(',')[2]?.trim() || ''
      },
      features: {
        bedrooms: property.bedrooms,
        bathrooms: property.bathrooms,
        area: property.area,
        hasGarage: (property.parking || 0) > 0,
        garageSpaces: property.parking || 0
      },
      images: (property._imovelData?.fotos && property._imovelData.fotos.length > 0) 
        ? property._imovelData.fotos 
        : [property.image || "https://images.unsplash.com/photo-1545324418-cc1a3fa10c00?w=400&h=300&fit=crop"],
      isHighlighted: property.featured,
      status: 'active' as const
    };

    const isFav = isFavorite(adaptedProperty.id);

    const toggleFav = (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      toggleFavorite({
        id: adaptedProperty.id,
        title: adaptedProperty.title,
        price: adaptedProperty.price,
        image: adaptedProperty.images[0],
        address: adaptedProperty.location.address,
        type: adaptedProperty.type,
        status: adaptedProperty.status,
        featured: adaptedProperty.isHighlighted
      });
    };

    // Render Modern Style (Default)
    if (cardStyle === 'modern') {
      return (
        <Link to={getPropertyUrl()} className="block group">
          <div className={`overflow-hidden h-full rounded-xl ${adaptedProperty.status !== 'active' ? 'opacity-70' : ''}`}>
            <div className="relative">
              <div className={`rounded-xl overflow-hidden ${isCompact ? 'h-[180px]' : 'h-[280px]'}`}>
                <PropertyImageCarousel
                  images={adaptedProperty.images}
                  alt={adaptedProperty.title}
                  imageClassName="transition-transform duration-500 group-hover:scale-110"
                  priority={priority}
                />
              </div>

              <div className="absolute top-3 left-3">
                <Badge className="rounded-full px-2.5 py-0.5 text-[10px] font-medium bg-[#F5F5F0] text-black border-none shadow-sm">
                  {adaptedProperty.type === 'sale' ? 'Venda' : 'Aluguel'}
                </Badge>
              </div>

              {adaptedProperty.isHighlighted && (
                <div className="absolute top-3 right-3">
                  <Badge className="rounded-full px-3 py-1 bg-white/90 text-primary flex items-center gap-1 backdrop-blur-sm shadow-sm">
                    <Star className="h-3 w-3 fill-current" />
                    <span className="text-xs font-medium">Destaque</span>
                  </Badge>
                </div>
              )}

              <button
                className="absolute right-3 top-3 h-9 w-9 bg-white/90 rounded-full flex items-center justify-center transition-all hover:bg-white hover:scale-105 backdrop-blur-sm shadow-sm z-20"
                onClick={toggleFav}
              >
                <Heart className={`h-[18px] w-[18px] transition-colors ${isFav ? 'fill-red-500 text-red-500' : 'text-gray-600'}`} />
              </button>
            </div>

            <div className="pt-3 pb-1">
              <div className="flex justify-between items-start gap-2">
                <h3 className={`font-medium text-foreground ${isCompact ? 'text-sm line-clamp-1' : 'text-base line-clamp-1'}`}>
                  {adaptedProperty.title}
                </h3>
                <div className="text-black dark:text-white font-bold whitespace-nowrap">
                  <span className={isCompact ? 'text-sm' : 'text-base'}>
                    {formatPrice(adaptedProperty.price, adaptedProperty.type as any)}
                  </span>
                </div>
              </div>

              <div className="flex items-center text-muted-foreground text-xs mt-1">
                <MapPin size={12} className="mr-1 shrink-0" />
                <span className="truncate">{`${adaptedProperty.location.address}, ${adaptedProperty.location.city}`}</span>
              </div>

              <div className="flex items-center gap-4 mt-3 pb-1 text-sm">
                <div className="flex items-center text-muted-foreground">
                  <Bed size={14} className="mr-1.5" />
                  <span>{adaptedProperty.features.bedrooms}</span>
                </div>
                <div className="flex items-center text-muted-foreground">
                  <Bath size={14} className="mr-1.5" />
                  <span>{adaptedProperty.features.bathrooms}</span>
                </div>
                <div className="flex items-center text-muted-foreground">
                  <Car size={14} className="mr-1.5" />
                  <span>{adaptedProperty.features.garageSpaces}</span>
                </div>
                <div className="flex items-center text-muted-foreground">
                  <Home size={14} className="mr-1.5" />
                  <span>{`${adaptedProperty.features.area}m²`}</span>
                </div>
              </div>
            </div>
          </div>
        </Link>
      );
    }

    // Render Classic Style
    if (cardStyle === 'classic') {
      return (
        <Link to={getPropertyUrl()} className="block h-full group">
          <Card className={`overflow-hidden h-full flex flex-col bg-card shadow-[0_8px_30px_rgba(0,0,0,0.05)] dark:shadow-[0_8px_30px_rgba(0,0,0,0.2)] hover:shadow-[0_15px_40px_rgba(0,0,0,0.08)] dark:hover:shadow-[0_15px_40px_rgba(0,0,0,0.3)] hover:-translate-y-1 transition-all duration-300 border-border/30 rounded-2xl ${adaptedProperty.status !== 'active' ? 'opacity-70' : ''}`}>
            <div className="relative p-3 pb-0">
              <div className={`overflow-hidden rounded-xl ${isCompact ? 'h-[160px]' : 'h-[200px]'}`}>
                <PropertyImageCarousel
                  images={adaptedProperty.images}
                  alt={adaptedProperty.title}
                  imageClassName="transition-transform duration-700 group-hover:scale-105"
                  priority={priority}
                />
              </div>
              
              <div className="absolute top-5 left-5">
                <Badge className="bg-white/95 text-black hover:bg-white font-medium px-2.5 py-0.5 rounded-full shadow-sm text-xs">
                  {adaptedProperty.type === 'sale' ? 'Venda' : 'Aluguel'}
                </Badge>
              </div>

              {adaptedProperty.isHighlighted && (
                <div className="absolute top-5 right-5">
                  <Badge className="bg-amber-500/95 text-white hover:bg-amber-500 border-none px-2.5 py-0.5 rounded-full shadow-sm gap-1 text-xs">
                    <Star className="h-3 w-3 fill-current" />
                    Destaque
                  </Badge>
                </div>
              )}
            </div>

            <CardContent className="p-4 flex flex-col flex-grow">
              <div className="flex-grow">
                <div className="flex justify-between items-start gap-2 mb-1">
                  <h3 className="font-semibold text-foreground text-base leading-tight line-clamp-1">
                    {adaptedProperty.title}
                  </h3>
                  <button
                    className="h-7 w-7 rounded-full flex items-center justify-center transition-all hover:bg-muted shrink-0 -mt-1 -mr-1"
                    onClick={toggleFav}
                  >
                    <Heart className={`h-[16px] w-[16px] transition-colors ${isFav ? 'fill-red-500 text-red-500' : 'text-foreground/40 hover:text-red-500'}`} />
                  </button>
                </div>
                
                <p className="text-muted-foreground text-[13px] line-clamp-1 mb-3">
                  {`${adaptedProperty.location.address}, ${adaptedProperty.location.city}`}
                </p>
                
                <div className="flex items-center text-muted-foreground text-[13px] gap-3 mb-4">
                  <div className="flex items-center gap-1.5" title={`${adaptedProperty.features.bedrooms} Quartos`}>
                    <Bed size={15} />
                    <span className="font-medium">{adaptedProperty.features.bedrooms}</span>
                  </div>
                  <div className="w-[1px] h-3 bg-border"></div>
                  <div className="flex items-center gap-1.5" title={`${adaptedProperty.features.bathrooms} Banheiros`}>
                    <Bath size={15} />
                    <span className="font-medium">{adaptedProperty.features.bathrooms}</span>
                  </div>
                  <div className="w-[1px] h-3 bg-border"></div>
                  <div className="flex items-center gap-1.5" title={`${adaptedProperty.features.garageSpaces} Vagas`}>
                    <Car size={15} />
                    <span className="font-medium">{adaptedProperty.features.garageSpaces}</span>
                  </div>
                  <div className="w-[1px] h-3 bg-border"></div>
                  <div className="flex items-center gap-1.5" title={`${adaptedProperty.features.area} m²`}>
                    <Home size={15} />
                    <span className="font-medium">{adaptedProperty.features.area}m²</span>
                  </div>
                </div>
              </div>

              <div className="pt-3 border-t border-border/40 mt-auto flex items-center justify-between gap-2 sm:gap-3">
                <div className="flex flex-col min-w-0 flex-1">
                  <span className="text-[10px] text-muted-foreground font-medium uppercase tracking-wider mb-0.5 truncate">Por apenas</span>
                  <span 
                    className="text-[15px] sm:text-base font-bold text-foreground leading-tight tracking-tight" 
                    title={formatPrice(adaptedProperty.price, adaptedProperty.type as any)}
                  >
                    {formatPrice(adaptedProperty.price, adaptedProperty.type as any)}
                  </span>
                </div>
                <Button 
                  className="rounded-xl font-medium px-3 sm:px-4 h-9 shadow-sm hover:shadow-md transition-all flex-shrink-0 whitespace-nowrap text-xs"
                  style={{ 
                    backgroundColor: branding?.primary_color || 'var(--brand-primary)', 
                    color: '#ffffff'
                  }}
                >
                  Ver Detalhes
                </Button>
              </div>
            </CardContent>
          </Card>
        </Link>
      );
    }

    // Render Minimalist Style
    return (
      <Link to={getPropertyUrl()} className="block group">
        <div className={`flex flex-col h-full ${adaptedProperty.status !== 'active' ? 'opacity-70' : ''}`}>
          <div className="relative rounded-2xl overflow-hidden mb-4">
            <div className={`overflow-hidden aspect-[4/3]`}>
              <PropertyImageCarousel
                images={adaptedProperty.images}
                alt={adaptedProperty.title}
                imageClassName="transition-transform duration-700 group-hover:scale-105"
                priority={priority}
              />
            </div>
            
            <div className="absolute top-4 left-4">
              <Badge className="bg-white/95 text-black hover:bg-white font-medium px-3 py-1 rounded-full shadow-sm border-none">
                {adaptedProperty.type === 'sale' ? 'Venda' : 'Aluguel'}
              </Badge>
            </div>

            <button
              className="absolute top-4 right-4 h-8 w-8 rounded-full flex items-center justify-center transition-all z-20 hover:scale-110"
              onClick={toggleFav}
            >
              <Heart className={`h-6 w-6 transition-colors drop-shadow-md ${isFav ? 'fill-red-500 text-red-500' : 'text-white'}`} />
            </button>
          </div>

          <div className="flex flex-col px-1">
            <div className="flex justify-between items-start gap-4">
              <h3 className="font-semibold text-foreground text-base line-clamp-1">
                {adaptedProperty.title}
              </h3>
              <div className="font-semibold text-foreground whitespace-nowrap">
                {formatPrice(adaptedProperty.price, adaptedProperty.type as any)}
              </div>
            </div>
            
            <p className="text-muted-foreground text-sm line-clamp-1 mt-1">
              {`${adaptedProperty.location.address}, ${adaptedProperty.location.city}`}
            </p>
            
            <div className="flex items-center text-muted-foreground text-sm gap-3 mt-2">
              <div className="flex items-center gap-1.5">
                <span className="font-medium text-foreground/80">{adaptedProperty.features.bedrooms}</span>
                <span>quartos</span>
              </div>
              <span className="w-1 h-1 rounded-full bg-border"></span>
              <div className="flex items-center gap-1.5">
                <span className="font-medium text-foreground/80">{adaptedProperty.features.bathrooms}</span>
                <span>banheiros</span>
              </div>
              {adaptedProperty.features.garageSpaces > 0 && (
                <>
                  <span className="w-1 h-1 rounded-full bg-border"></span>
                  <div className="flex items-center gap-1.5">
                    <span className="font-medium text-foreground/80">{adaptedProperty.features.garageSpaces}</span>
                    <span>vagas</span>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </Link>
    );
  },
  (prevProps, nextProps) => {
    return (
      prevProps.property.id === nextProps.property.id &&
      prevProps.property.title === nextProps.property.title &&
      prevProps.property.price === nextProps.property.price &&
      prevProps.property.status === nextProps.property.status &&
      prevProps.property.featured === nextProps.property.featured &&
      prevProps.variant === nextProps.variant &&
      prevProps.priority === nextProps.priority
    );
  }
);

PropertyCard.displayName = 'MarketplacePropertyCard';

export default PropertyCard;
